import { default as React } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import RestoreIcon from '@mui/icons-material/Restore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { useCurrentUserEmail, useSkyStageAdminEmails } from './hooks';
import { isUserAuthorizedForEntity } from './hooks/isUserAuthorizedForEntity';

interface BackupSnapshot {
  name: string;
  trigger: 'daily' | 'pre-deploy' | 'pre-restore';
  createdAt: string;
  readyToUse: boolean;
}

const triggerLabels: Record<string, string> = {
  daily: 'Daily',
  'pre-deploy': 'Pre-deploy',
  'pre-restore': 'Pre-restore',
};

/**
 * Format a storage size string like "10Gi" to "10 GB".
 */
function formatStorageSize(raw: string | undefined): string {
  if (!raw) return 'unknown';
  const match = raw.match(/^(\d+)\s*(Gi|G|Mi|M)$/i);
  if (!match) return raw;
  const value = match[1];
  const unit = match[2].toLowerCase();
  if (unit === 'gi' || unit === 'g') return `${value} GB`;
  if (unit === 'mi' || unit === 'm') return `${value} MB`;
  return raw;
}

/**
 * Format an ISO date string to "Apr 3, 2026 \u2014 2:00 AM" style.
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart} \u2014 ${timePart}`;
}

export const BackupsTab = () => {
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const currentUserEmail = useCurrentUserEmail();
  const adminEmails = useSkyStageAdminEmails();

  const [snapshots, setSnapshots] = useState<BackupSnapshot[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<BackupSnapshot | null>(
    null,
  );
  const [confirmationInput, setConfirmationInput] = useState('');
  const [restoreSuccess, setRestoreSuccess] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoreProgress, setRestoreProgress] = useState<string | null>(null);
  const [restoringSnapshotName, setRestoringSnapshotName] = useState<
    string | null
  >(null);
  const appName = entity.metadata.name;
  const storageSize =
    entity.metadata.annotations?.['microapp/storageSize'] || '';
  const storageClassName =
    entity.metadata.annotations?.['microapp/storageClassName'] || '';
  const isLegacyStorage =
    !storageClassName || storageClassName === 'ssd-regional';
  const isAuthorized = isUserAuthorizedForEntity(
    entity,
    currentUserEmail,
    adminEmails,
  );

  const fetchSnapshots = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const backendUrl = configApi.getString('backend.baseUrl');
      const apiUrl = `${backendUrl}/api/microapps-api/backups/${encodeURIComponent(
        appName,
      )}`;
      const { token } = await identityApi.getCredentials();

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch backups (${response.status})`);
      }

      const result = await response.json();
      setSnapshots(result.backups || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [appName, configApi, identityApi]);

  useEffect(() => {
    fetchSnapshots();
  }, [fetchSnapshots]);

  const handleOpenRestoreDialog = (snapshot: BackupSnapshot) => {
    setRestoreTarget(snapshot);
    setConfirmationInput('');
  };

  const handleCloseRestoreDialog = () => {
    if (!isRestoring) {
      setRestoreTarget(null);
      setConfirmationInput('');
    }
  };

  const pollRestoreStatus = useCallback(
    async (restoreId: string, restoringDate: string) => {
      const backendUrl = configApi.getString('backend.baseUrl');
      const statusUrl = `${backendUrl}/api/microapps-api/backups/${encodeURIComponent(
        appName,
      )}/restore/${encodeURIComponent(restoreId)}`;

      const startTime = Date.now();
      const MAX_POLL_MS = 20 * 60 * 1000;
      let consecutiveErrors = 0;

      const poll = async () => {
        if (Date.now() - startTime > MAX_POLL_MS) {
          setIsRestoring(false);
          setRestoreProgress(null);
          setRestoringSnapshotName(null);
          setRestoreError(
            'Restore status check timed out. The restore may still be running — please refresh the page to check.',
          );
          return;
        }

        try {
          const { token } = await identityApi.getCredentials();
          const res = await fetch(statusUrl, {
            headers: {
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          });

          if (!res.ok) {
            if (res.status === 404) {
              setIsRestoring(false);
              setRestoreProgress(null);
              setRestoringSnapshotName(null);
              setRestoreError(
                'Lost connection to restore process. Please refresh the page to check current status.',
              );
              return;
            }
            consecutiveErrors++;
            if (consecutiveErrors >= 5) {
              setIsRestoring(false);
              setRestoreProgress(null);
              setRestoringSnapshotName(null);
              setRestoreError(
                `Unable to check restore status (HTTP ${res.status}). Please refresh the page.`,
              );
              return;
            }
            setTimeout(poll, 5000);
            return;
          }

          consecutiveErrors = 0;
          const status = await res.json();

          if (status.status === 'completed') {
            setIsRestoring(false);
            setRestoreProgress(null);
            setRestoringSnapshotName(null);
            setRestoreSuccess(restoringDate);
            await fetchSnapshots();
            return;
          }

          if (status.status === 'failed') {
            setIsRestoring(false);
            setRestoreProgress(null);
            setRestoringSnapshotName(null);
            setRestoreError(status.message);
            return;
          }

          // Still in progress — update message and poll again
          setRestoreProgress(status.message);
          setTimeout(poll, 5000);
        } catch {
          consecutiveErrors++;
          if (consecutiveErrors >= 5) {
            setIsRestoring(false);
            setRestoreProgress(null);
            setRestoringSnapshotName(null);
            setRestoreError(
              'Lost network connection while checking restore status. Please refresh the page.',
            );
            return;
          }
          setTimeout(poll, 5000);
        }
      };

      // Start polling after a short delay
      setTimeout(poll, 3000);
    },
    [appName, configApi, identityApi, fetchSnapshots],
  );

  const handleRestore = async () => {
    if (!restoreTarget) return;
    const restoringDate = formatDate(restoreTarget.createdAt);

    setIsRestoring(true);
    setRestoringSnapshotName(restoreTarget.name);
    setRestoreSuccess(null);
    setRestoreError(null);
    setRestoreProgress('Starting restore...');

    try {
      const backendUrl = configApi.getString('backend.baseUrl');
      const apiUrl = `${backendUrl}/api/microapps-api/backups/${encodeURIComponent(
        appName,
      )}/restore`;
      const { token } = await identityApi.getCredentials();

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5 * 60 * 1000);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ snapshotName: restoreTarget.name }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(
          result.error ||
            result.message ||
            `Failed to restore (${response.status})`,
        );
      }

      const result = await response.json();

      setRestoreTarget(null);
      setConfirmationInput('');

      // Start polling for real status
      pollRestoreStatus(result.restoreId, restoringDate);
    } catch (err) {
      const errorMessage = (err as Error).message;
      setIsRestoring(false);
      setRestoreProgress(null);
      setRestoringSnapshotName(null);
      setRestoreError(`Failed to restore: ${errorMessage}`);
    }
  };

  if (!isAuthorized) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          You do not have permission to view backups for this application.
        </Alert>
      </Box>
    );
  }

  const getTooltipTitle = (snapshot: BackupSnapshot): string => {
    if (!snapshot.readyToUse) return 'Backup is not ready yet';
    if (isRestoring) return 'Another restore is in progress';
    return '';
  };

  const renderRestoreAction = (snapshot: BackupSnapshot, index: number) => {
    if (index === 0) {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontStyle: 'italic' }}
        >
          Active version
        </Typography>
      );
    }

    if (restoringSnapshotName === snapshot.name) {
      return (
        <Button
          size="small"
          variant="outlined"
          color="info"
          disabled
          startIcon={<CircularProgress size={14} />}
          sx={{ textTransform: 'none' }}
        >
          Restoring...
        </Button>
      );
    }

    return (
      <Tooltip
        title={getTooltipTitle(snapshot)}
        disableHoverListener={snapshot.readyToUse && !isRestoring}
      >
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={() => handleOpenRestoreDialog(snapshot)}
            disabled={!snapshot.readyToUse || isRestoring || isLegacyStorage}
            aria-label={`Restore backup from ${formatDate(snapshot.createdAt)}`}
            sx={{ textTransform: 'none' }}
          >
            Restore
          </Button>
        </span>
      </Tooltip>
    );
  };

  const isConfirmValid = confirmationInput === appName;

  return (
    <Box sx={{ p: 3 }}>
      {/* Restore In Progress Banner */}
      {restoreProgress && (
        <Alert
          severity="info"
          icon={<CircularProgress size={20} />}
          sx={{
            mb: 3,
            borderLeft: 4,
            borderLeftColor: 'info.main',
          }}
        >
          <AlertTitle>Restore in progress</AlertTitle>
          {restoreProgress}
          <br />
          This may take several minutes. Do not close this page.
          <LinearProgress sx={{ mt: 1.5, borderRadius: 1 }} />
        </Alert>
      )}

      {/* Restore Success Banner */}
      {restoreSuccess && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          action={
            <IconButton
              aria-label="close restore success message"
              color="inherit"
              size="small"
              onClick={() => setRestoreSuccess(null)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{
            mb: 3,
            borderLeft: 4,
            borderLeftColor: 'success.main',
          }}
        >
          <AlertTitle>Restore completed successfully</AlertTitle>
          Your app's persistent storage has been restored to the backup from{' '}
          {restoreSuccess}. A pre-restore backup of your previous data was saved
          automatically. Your app has been restarted and is now running.
        </Alert>
      )}

      {/* Restore Error Banner */}
      {restoreError && (
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close restore error message"
              color="inherit"
              size="small"
              onClick={() => setRestoreError(null)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{
            mb: 3,
            borderLeft: 4,
            borderLeftColor: 'error.main',
          }}
        >
          <AlertTitle>Restore failed</AlertTitle>
          {restoreError}
        </Alert>
      )}

      {/* Top Banner */}
      {isLegacyStorage ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Backup &amp; Restore not available</AlertTitle>
          This app uses legacy storage that does not support snapshots. Please
          contact the platform team to migrate your storage manually.
        </Alert>
      ) : (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
          Automatic backups are enabled. Your persistent storage (
          <Box
            component="code"
            sx={{
              bgcolor: 'action.hover',
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              fontFamily: 'monospace',
              fontSize: '0.85em',
            }}
          >
            /data
          </Box>
          , {formatStorageSize(storageSize)} SSD) is backed up daily and before
          each redeployment. Backups are retained for 14 days.
        </Alert>
      )}

      {/* Section Title */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Backup History
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Showing last 14 days
        </Typography>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {!isLoading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Empty State */}
      {!isLoading && !error && snapshots.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No backups found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Backups will appear here after the first scheduled or pre-deployment
            backup runs.
          </Typography>
        </Box>
      )}

      {/* Backup Table */}
      {!isLoading && !error && snapshots.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    DATE & TIME
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    TRIGGER
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    SIZE
                  </Typography>
                </TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {snapshots.map((snapshot, index) => (
                <TableRow
                  key={snapshot.name}
                  hover
                  sx={
                    index === 0
                      ? {
                          bgcolor: 'action.hover',
                          borderLeft: 3,
                          borderLeftColor: 'success.main',
                        }
                      : undefined
                  }
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {formatDate(snapshot.createdAt)}
                      </Typography>
                      {index === 0 && (
                        <Chip
                          label="Current"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {triggerLabels[snapshot.trigger] || snapshot.trigger}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatStorageSize(storageSize)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {renderRestoreAction(snapshot, index)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={restoreTarget !== null}
        onClose={handleCloseRestoreDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="restore-dialog-title"
      >
        <DialogTitle id="restore-dialog-title" sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon color="warning" />
            <Typography variant="h6" component="span">
              Restore from Backup
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            A backup of your current data will be created automatically before
            the restore begins.
          </Alert>

          <Typography variant="body2" sx={{ mb: 3 }}>
            This will replace your app's persistent storage with the backup from
            the selected date. Your app will be temporarily unavailable during
            the restore.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                App
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {appName}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Restoring To
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {restoreTarget ? formatDate(restoreTarget.createdAt) : ''}
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2" sx={{ mb: 1 }}>
            To confirm, type the app name{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              {appName}
            </Box>{' '}
            below:
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={confirmationInput}
            onChange={e => setConfirmationInput(e.target.value)}
            placeholder={appName}
            disabled={isRestoring}
            aria-label="Type app name to confirm restore"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseRestoreDialog}
            disabled={isRestoring}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRestore}
            color="error"
            variant="contained"
            disabled={!isConfirmValid || isRestoring}
            startIcon={
              isRestoring ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <RestoreIcon />
              )
            }
            sx={{ textTransform: 'none' }}
          >
            Restore Backup
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
